import { useState, useCallback, useRef } from 'react';
import { streamChatMessageWithHistory, ApiMessage, PortfolioContext, InvestorPersona } from '../apis/backend';
import { StreamEvent, CollectionCardsEvent, ToolOutputEvent } from '../types/streaming';

export interface UseStreamingChatReturn {
  isLoading: boolean;
  progressText: string;
  streamingMessage: string;
  reasoningText: string;
  showReasoning: boolean;
  finalResponse: string | null;
  collectionCards: CollectionCardsEvent | null;
  toolOutputs: ToolOutputEvent[];
  latestTodoOutput: ToolOutputEvent | null;
  currentTool: string | null;
  activeTools: { [key: string]: { name: string; args?: any; startTime: number } };
  submitQuery: (
    messages: ApiMessage[],
    portfolioContext?: PortfolioContext,
    investorPersona?: InvestorPersona,
    model?: string,
    onConnected?: () => void,
    language?: string,
  ) => Promise<boolean>;
  abortQuery: () => void;
  resetQuery: () => void;
}

export const useStreamingChat = (): UseStreamingChatReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [progressText, setProgressText] = useState('');
  const [streamingMessage, setStreamingMessage] = useState('');
  const [reasoningText, setReasoningText] = useState('');
  const [showReasoning, setShowReasoning] = useState(false);
  const [finalResponse, setFinalResponse] = useState<string | null>(null);
  const [collectionCards, setCollectionCards] = useState<CollectionCardsEvent | null>(null);
  const [toolOutputs, setToolOutputs] = useState<ToolOutputEvent[]>([]);
  const [latestTodoOutput, setLatestTodoOutput] = useState<ToolOutputEvent | null>(null);
  const [currentTool, setCurrentTool] = useState<string | null>(null);
  const [activeTools, setActiveTools] = useState<{ [key: string]: { name: string; args?: any; startTime: number } }>(
    {},
  );

  const submitQuery = useCallback(
    async (
      messages: ApiMessage[],
      portfolioContext?: PortfolioContext,
      investorPersona?: InvestorPersona,
      model?: string,
      onConnected?: () => void,
      language?: string,
    ) => {
      // Abort any existing request before starting a new one
      abortControllerRef.current?.abort();

      // Capture controller locally to avoid race conditions in finally block
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      setProgressText('searching...');
      setStreamingMessage('');
      setReasoningText('');
      setShowReasoning(false);
      setFinalResponse(null);
      setCollectionCards(null);
      setToolOutputs([]);
      setLatestTodoOutput(null);
      setCurrentTool(null);
      setActiveTools({});

      let accumulatedMessage = ''; // Use closure variable to track message
      let streamError = false; // Track if error event received during streaming

      const handleStreamEvent = (event: StreamEvent | any) => {
        switch (event.type) {
          case 'connected':
            setProgressText('connected...');
            onConnected?.();
            break;

          case 'tool_call':
            // Update progress text based on tool being called
            const toolName = event.content?.tool?.name || event.name || event.tool_name || 'tool';
            const toolArgs = event.content?.tool?.args || {};
            const toolId = `${toolName}_${Date.now()}`;

            setCurrentTool(toolName);

            // Add to active tools immediately for real-time display
            setActiveTools((prev) => ({
              ...prev,
              [toolId]: {
                name: toolName,
                args: toolArgs,
                startTime: Date.now(),
              },
            }));

            // Create immediate tool output to show loading state
            const loadingToolOutput: ToolOutputEvent = {
              type: 'tool_output',
              output: JSON.stringify({ loading: true, tool_name: toolName }),
              tool_name: toolName,
              timestamp: new Date().toISOString(),
            };

            // Handle todo_write separately - replace instead of append
            if (toolName === 'todo_write') {
              setLatestTodoOutput(loadingToolOutput);
            } else {
              setToolOutputs((prev) => [...prev, loadingToolOutput]);
            }

            if (toolName.toLowerCase().includes('search')) {
              setProgressText('searching...');
            } else if (toolName.toLowerCase().includes('analyz')) {
              setProgressText('analyzing data...');
            } else if (toolName.toLowerCase().includes('news')) {
              setProgressText('fetching news...');
            } else if (toolName.toLowerCase().includes('todo')) {
              setProgressText('updating tasks...');
            } else if (toolName.toLowerCase().includes('read')) {
              setProgressText('reading documents...');
            } else if (toolName.toLowerCase().includes('collection')) {
              setProgressText('finding collections...');
            } else {
              setProgressText('processing...');
            }
            break;

          case 'tool_output':
            // Handle tool output events - replace loading state with actual output
            const completedToolOutput: ToolOutputEvent = {
              type: 'tool_output',
              output: event.output || '',
              tool_name: event.tool_name || currentTool || undefined,
              timestamp: new Date().toISOString(),
            };

            const outputToolName = event.tool_name || currentTool;

            // Handle todo_write separately - always replace latest
            if (outputToolName === 'todo_write') {
              setLatestTodoOutput(completedToolOutput);
            } else {
              // Replace the loading tool output with the completed one for non-todo tools
              setToolOutputs((prev) => {
                const updated = [...prev];

                // Find and replace the most recent loading output for this tool
                for (let i = updated.length - 1; i >= 0; i--) {
                  const existing = updated[i];
                  // Check if this is a loading output for the same tool
                  const isLoadingOutput =
                    existing.output.includes('"loading":true') || existing.output.includes('"loading": true');
                  const isSameTool =
                    existing.tool_name === outputToolName ||
                    existing.tool_name === currentTool ||
                    existing.tool_name === event.tool_name;

                  if (isSameTool && isLoadingOutput) {
                    updated[i] = completedToolOutput;
                    return updated;
                  }
                }
                // If no loading state found, just add the output
                return [...prev, completedToolOutput];
              });
            }

            // Remove from active tools
            setActiveTools((prev) => {
              const updated = { ...prev };
              Object.keys(updated).forEach((key) => {
                if (updated[key].name === currentTool) {
                  delete updated[key];
                }
              });
              return updated;
            });

            // Update progress text based on tool completion
            if (currentTool) {
              if (currentTool.toLowerCase().includes('search')) {
                setProgressText('search completed');
              } else if (currentTool.toLowerCase().includes('news')) {
                setProgressText('news retrieved');
              } else if (currentTool.toLowerCase().includes('todo')) {
                setProgressText('tasks updated');
              } else {
                setProgressText('tool completed');
              }
            }
            break;

          case 'reasoning_delta':
            // Accumulate reasoning text (shown as preview)
            setReasoningText((prev) => prev + (event.delta || ''));
            setShowReasoning(true); // Show reasoning block
            setProgressText('thinking...'); // Show thinking state
            break;

          case 'token_delta':
            // Accumulate token-by-token streaming (final response)
            accumulatedMessage += event.delta || '';
            setStreamingMessage(accumulatedMessage);
            setProgressText(''); // Clear progress text when content starts arriving
            // Keep reasoning text visible during streaming - don't clear it here
            break;

          case 'message_complete':
            // Final complete message
            accumulatedMessage = event.content || '';
            setStreamingMessage(accumulatedMessage);
            setFinalResponse(accumulatedMessage);
            setProgressText('');
            setShowReasoning(false); // Hide reasoning block when final message is complete
            setIsLoading(false);
            break;

          case 'agent_updated':
            setProgressText('analyzing...');
            break;

          case 'agent_text':
            // Legacy support - accumulate text
            accumulatedMessage += event.text || '';
            setStreamingMessage(accumulatedMessage);
            setProgressText('');
            break;

          case 'message':
            // Legacy support - handle complete message content
            accumulatedMessage += event.content || '';
            setStreamingMessage(accumulatedMessage);
            setProgressText('');
            break;

          case 'collection_cards':
            // Handle collection cards response
            setCollectionCards(event as CollectionCardsEvent);
            setProgressText('');
            break;

          case 'complete':
            // Set final response and stop loading - use accumulated message
            setFinalResponse(accumulatedMessage);
            setShowReasoning(false); // Hide reasoning block on completion
            setIsLoading(false);
            break;

          case 'error':
            streamError = true;
            setFinalResponse('Sorry, an error occurred while processing your request.');
            setIsLoading(false);
            break;

          case 'retry_reset':
            // Clear text streaming state on retry
            accumulatedMessage = '';
            setStreamingMessage('');
            setReasoningText('');
            setShowReasoning(false);
            // Fix #7: Clear tool outputs on retry to prevent duplicates when server re-sends them
            // The server will re-emit tool_call/tool_output events on retry, so keeping
            // stale ones causes duplicates in the UI.
            setCollectionCards(null);
            setToolOutputs([]);
            setLatestTodoOutput(null);
            setCurrentTool(null);
            setActiveTools({});
            break;

          case 'retry':
            // Show retry status to user
            setProgressText(`Reconnecting (attempt ${event.attempt}/${event.maxRetries})...`);
            break;

          case 'aborted':
            // User cancelled the request - stop loading without showing error
            setIsLoading(false);
            setProgressText('');
            break;
        }
      };

      try {
        await streamChatMessageWithHistory(
          messages,
          handleStreamEvent,
          controller.signal,
          portfolioContext,
          investorPersona,
          model,
          language,
        );
        // Always clear loading state when stream ends, even if no complete/error event
        // was received (e.g. Gunicorn kills worker via SIGABRT → TCP FIN → reader.read()
        // returns done:true without dispatching any cleanup event).
        // Guard setFinalResponse so an error message set by the 'error' event handler
        // is never overwritten by partial accumulated content.
        if (!streamError) {
          setFinalResponse(accumulatedMessage);
          setShowReasoning(false);
        }
        setIsLoading(false);
        return !streamError;
      } catch (error) {
        setFinalResponse('Sorry, an error occurred while processing your request.');
        setIsLoading(false);
        return false;
      } finally {
        // Only clear if this is still our controller (not replaced by a newer query)
        if (abortControllerRef.current === controller) {
          abortControllerRef.current = null;
        }
      }
    },
    [currentTool],
  );

  const abortQuery = useCallback(() => {
    // Just abort - cleanup is handled by submitQuery's finally block
    abortControllerRef.current?.abort();
  }, []);

  const resetQuery = useCallback(() => {
    setIsLoading(false);
    setProgressText('');
    setStreamingMessage('');
    setReasoningText('');
    setShowReasoning(false);
    setFinalResponse(null);
    setCollectionCards(null);
    setToolOutputs([]);
    setLatestTodoOutput(null);
    setCurrentTool(null);
    setActiveTools({});
  }, []);

  return {
    isLoading,
    progressText,
    streamingMessage,
    reasoningText,
    showReasoning,
    finalResponse,
    collectionCards,
    toolOutputs,
    latestTodoOutput,
    currentTool,
    activeTools,
    submitQuery,
    abortQuery,
    resetQuery,
  };
};
